import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  clipDownloadables,
  downloadClip,
  getClipLicenses,
  licenseClip,
} from '../../actions/clips';
import { buildAction } from '../../actions/helpers';
import {
  getActiveSubscription,
  subscribe,
  setChosenPlan,
  setCouponCode,
  getPlanPrice,
} from '../../actions/plan';
import { hideModal } from '../../actions/modal';
import {
  EMPTY_ARRAY,
  PLAN_STATUS,
  MODAL_LAYOUT,
  LICENSING_STEP,
  SET_PAYMENT_ERROR,
  RESPONSIVE_SIZE,
} from '../../constants';
import { themeColors } from '../../lib/Color';
import facebook from '../../lib/facebook';
import { Brandmark } from '../../components/Icons';
import ClipDownloading from '../../components/ClipDownloading';
import ElementTransition from '../../components/ElementTransition';
import Licensing from '../../components/Licensing';
import Modal from '../../components/Modal';
import Video from '../../components/Video';
import Style from '../../components/Style';
import { L1, L2, L3, L4, H3, H5, P1 } from '../../components/Typography';

class LicensingModal extends React.PureComponent {
  static propTypes = {
    clip: PropTypes.object,
    usingLicense: PropTypes.object,
    backTo: PropTypes.string,
    onBackTo: PropTypes.func,
    goTo: PropTypes.string,
    onGoTo: PropTypes.func,
    onClose: PropTypes.func,
    refreshSubscription: PropTypes.func,
    hasSubscription: PropTypes.bool,
    canLicense: PropTypes.bool,
    pastDue: PropTypes.bool,
    chosenPlan: PropTypes.object,
    couponCode: PropTypes.string,
    couponError: PropTypes.string,
    activePlan: PropTypes.object,
    planSummary: PropTypes.object,
    paymentError: PropTypes.string,
    onChangePlan: PropTypes.func,
    onChangeCoupon: PropTypes.func,
    onConfirmPlan: PropTypes.func,
    onUpdateSubscription: PropTypes.func,
    onLicenseClip: PropTypes.func,
    onGetDownloadables: PropTypes.func,
    onDownloadClip: PropTypes.func,
  };
  static defaultProps = {};

  constructor(props) {
    super(props);

    this.state = {
      // Only set on initial render.
      alreadyLicensed: props.usingLicense !== undefined,
      // Will be set if they license the clip.
      downloadLicense: props.usingLicense,
      licensingError: '',
      overrideStep: undefined,
      desiredVersion: undefined,
      downloadUrl: undefined,
      downloadablesFetching: false,
      downloadables: {
        allVersions: EMPTY_ARRAY,
        downloadableVerions: EMPTY_ARRAY,
      },
      colors: themeColors(this.props.clip.scene_colors),
    };
  }

  componentWillMount() {
    if (this.state.downloadLicense) {
      this.doGetDownloadables(this.props.clip.clip_id);
    }
    this.props.refreshSubscription();
  }

  parseLicensingError = err => {
    return String(err);
  };

  doGetDownloadables = clipId => {
    this.setState({ downloadablesFetching: true });
    return this.props
      .onGetDownloadables(this.props.clip.clip_id)
      .then(downloadables =>
        this.setState({
          downloadables: downloadables,
          downloadablesFetching: false,
        }),
      );
  };

  doLicenseClip = description => {
    let stateUpdate = {};
    return this.props
      .onLicenseClip(this.props.clip.clip_id, description)
      .then(license => (stateUpdate['downloadLicense'] = license))
      .catch(err => {
        stateUpdate['licensingError'] = this.parseLicensingError(err);
        throw err;
      })
      .then(() => this.doGetDownloadables(this.props.clip.clips_id))
      .then(() => this.setState(stateUpdate))
      .catch(err => {
        this.setState(stateUpdate);
        throw err;
      });
  };

  doPickVersion = version => {
    this.setState({ desiredVersion: version });
  };

  doDownloadClip = () => {
    const version = this.state.desiredVersion;

    if (!this.state.downloadables.downloadableVerions.includes(version)) {
      this.setState({ overrideStep: LICENSING_STEP.PLAN });
      return Promise.resolve();
    }

    return this.props
      .onDownloadClip(
        this.props.clip.clip_id,
        version,
        this.state.downloadLicense.licensedAt,
      )
      .then(url => {
        this.setState({
          downloadUrl: url,
          overrideStep: LICENSING_STEP.DOWNLOADING,
        });
        window.location = url;
      });
  };

  doUpdateSubscription = (tokenId, plan) => {
    return this.props
      .onUpdateSubscription(tokenId, plan)
      .then(() => this.props.refreshSubscription())
      .then(() => {
        if (this.state.downloadLicense) {
          return this.doGetDownloadables(this.props.clip.clip_id);
        } else {
          return Promise.resolve();
        }
      })
      .then(() => this.setState({ overrideStep: undefined }));
  };

  doConfirmPlan = plan => {
    return this.props
      .onConfirmPlan(plan)
      .then(() => this.setState({ overrideStep: LICENSING_STEP.PAY }));
  };

  doChangePlan = plan => {
    if (this.props.hasSubscription) {
      // Lockout changing the interval of an active subscription.
      return this.props.onChangePlan({ name: plan.name });
    } else {
      return this.props.onChangePlan(plan);
    }
  };

  getStep = () => {
    // That's why they call it an override.
    if (this.state.overrideStep !== undefined) {
      return this.state.overrideStep;
    }

    if (this.state.downloadLicense !== undefined) {
      return LICENSING_STEP.PICK_VERSION;
    }

    if (!this.props.hasSubscription) {
      return LICENSING_STEP.PLAN;
    }

    if (this.props.canLicense) {
      return LICENSING_STEP.LICENSE_DESCRIPTION;
    }

    // No existing license and cannot create a new license.
    return LICENSING_STEP.OUT_OF_LICENSES;
  };

  getLayout = step => {
    switch (step) {
      case LICENSING_STEP.DOWNLOADING:
        return MODAL_LAYOUT.FULL;
      case LICENSING_STEP.PLAN:
      case LICENSING_STEP.PAY:
        return MODAL_LAYOUT.RIGHT;
      default:
        return MODAL_LAYOUT.EQUAL_SPLIT;
    }
  };

  render() {
    const {
      clip,
      usingLicense,
      backTo,
      onBackTo,
      goTo,
      onGoTo,
      onClose,
      refreshSubscription,
      hasSubscription,
      canLicense,
      pastDue,
      chosenPlan,
      couponCode,
      couponError,
      activePlan,
      planSummary,
      paymentError,
      onChangePlan,
      onChangeCoupon,
      onConfirmPlan,
      onUpdateSubscription,
      onLicenseClip,
      onDownloadClip,
      ...otherProps
    } = this.props;

    const step = this.getStep();
    const layout = this.getLayout(step);
    let leftContent = {
      id: 'description',
      element: (
        <ClipDescription
          clip={clip}
          license={this.state.alreadyLicensed ? usingLicense : undefined}
        />
      ),
    };

    if (layout === MODAL_LAYOUT.RIGHT) {
      leftContent = { id: 'logo', element: <Logo /> };
    }

    return (
      <Modal
        onClose={onClose}
        layout={layout}
        colors={this.state.colors}
        backgroundContent={<VideoBackground clip={clip} />}
        fullContent={
          <ClipDownloading
            downloadUrl={this.state.downloadUrl}
            backTo={backTo}
            onBackTo={onBackTo}
            goTo={goTo}
            onGoTo={onGoTo}
          />
        }
        leftContent={
          <ElementTransition
            {...leftContent}
            optionsLeave={{ stiffness: 300 }}
          />
        }
        rightContent={
          <Licensing
            step={step}
            onCancel={onClose}
            onLicenseClip={this.doLicenseClip}
            licensingError={this.state.licensingError}
            onPickVersion={this.doPickVersion}
            desiredVersion={this.state.desiredVersion}
            downloadables={this.state.downloadables}
            downloadablesFetching={this.state.downloadablesFetching}
            onDownloadClip={this.doDownloadClip}
            downloadClipError={this.state.downloadClipError}
            alreadyLicensed={this.state.alreadyLicensed}
            pastDue={pastDue}
            chosenPlan={chosenPlan}
            couponCode={couponCode}
            couponError={couponError}
            activePlan={activePlan}
            planSummary={planSummary}
            hasSubscription={hasSubscription}
            paymentError={paymentError}
            onGoToPlan={() =>
              this.setState({ overrideStep: LICENSING_STEP.PLAN })}
            onChangePlan={this.doChangePlan}
            onChangeCoupon={onChangeCoupon}
            onConfirmPlan={this.doConfirmPlan}
            onUpdateSubscription={this.doUpdateSubscription}
          />
        }
      />
    );
  }
}

function mapStateToProps(state) {
  const hasSubscription =
    state.plan.activePlan.status !== undefined &&
    state.plan.activePlan.status !== PLAN_STATUS.CANCELED;

  const inGoodStanding = [PLAN_STATUS.TRIALING, PLAN_STATUS.ACTIVE].includes(
    state.plan.activePlan.status,
  );

  let canLicense =
    inGoodStanding &&
    (state.user.subscription.licensesMax < 0 ||
      state.user.subscription.licensesMax >
        state.user.subscription.licensesUsed);

  return {
    hasSubscription: hasSubscription,
    canLicense: canLicense,
    pastDue: state.plan.activePlan.status === PLAN_STATUS.PAST_DUE,
    chosenPlan: state.plan.chosenPlan,
    couponCode: state.plan.couponCode,
    couponError: state.plan.couponError,
    activePlan: state.plan.activePlan,
    paymentError: state.plan.paymentError,
    planSummary: state.plan.chosenSummary,
    ...state.modal.props,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    refreshSubscription: () => dispatch(getActiveSubscription()),
    onClose: () => dispatch(hideModal()),
    onChangePlan: plan => dispatch(setChosenPlan(plan)),
    onChangeCoupon: code => dispatch(setCouponCode(code)),
    onConfirmPlan: plan => {
      facebook.track('AddToCart');
      facebook.track('InitiateCheckout');
      dispatch(buildAction(SET_PAYMENT_ERROR, undefined));
      return dispatch(setChosenPlan(plan));
    },
    onUpdateSubscription: (stripeTokenId, plan) =>
      dispatch(subscribe(stripeTokenId, plan))
        .then(() => {
          if (stripeTokenId) {
            facebook.track('AddPaymentInfo');
          }
          facebook.track('Purchase', {
            value: getPlanPrice(plan.name),
            currency: 'USD',
          });
        })
        .catch(err => {
          dispatch(buildAction(SET_PAYMENT_ERROR, err.message));
          throw err;
        }),
    onLicenseClip: (clipId, description) =>
      dispatch(licenseClip(clipId, description)),
    onGetDownloadables: clipId => dispatch(clipDownloadables(clipId)),
    onDownloadClip: (clipId, version, licenseDate) =>
      dispatch(downloadClip(clipId, version, licenseDate)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LicensingModal);

function VideoBackground({ clip }) {
  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        backgroundColor: Style.Colors.black,
      }}
    >
      <Video
        src={clip.clip_preview_large}
        poster={clip.clip_poster}
        style={{
          height: 'calc(100% + 1px)',
          opacity: 0.6,
        }}
      />
    </div>
  );
}

function ClipDescriptionComp({ responsive, clip, license }) {
  return (
    <div
      style={{
        opacity: responsive >= RESPONSIVE_SIZE.TABLET_LANDSCAPE ? 1 : 0,
        padding: '10rem 3rem 3rem',
        textAlign: 'center',
        color: Style.Colors.white,
      }}
    >
      <L2 style={{ maxWidth: '35rem', margin: 'auto', opacity: 0.75 }}>
        {clip.scene_name.toUpperCase()}
      </L2>
      <H5 style={{ maxWidth: '40rem', margin: '2rem auto', fontWeight: 500 }}>
        {clip.clip_name}
      </H5>
      {license && (
        <div>
          <L1 style={{ display: 'block', marginTop: '8rem' }}>
            licensed: {moment(license.licensedAt).format('L')}
          </L1>
          <L4 style={{ display: 'block', marginTop: '4rem', opacity: 0.75 }}>
            LICENSE DESCRIPTION
          </L4>
          <P1 style={{ marginTop: '1rem' }}>{license.description}</P1>
        </div>
      )}
    </div>
  );
}

function mapStateToClipDescriptionProps(state) {
  return {
    responsive: state.runtime.responsive,
  };
}

const ClipDescription = connect(mapStateToClipDescriptionProps)(
  ClipDescriptionComp,
);

function Logo() {
  return (
    <div
      style={{
        padding: '6rem 0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
      }}
    >
      <Brandmark color={Style.Colors.white} />
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/containers/LicensingModal/LicensingModal.js